/**
 * Job Seeker Signup - Free Plan
 * Velaigal rebrand. Editorial 50/50 split mirroring LoginPage.
 *
 * Account creation only — phone/location/bio are collected later via the
 * profile flow, not at signup. Submits straight to the session API and
 * shows the uniform "check your email" panel on success.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { signup, resendVerification } from '../../lib/authApi';
import { ApiError } from '../../lib/api';
import Wordmark from '../../components/Brand/Wordmark';

const JobSeekerSignup: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';

    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';

    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 10) newErrors.password = 'At least 10 characters';

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!validateForm()) return;

    setBusy(true);
    try {
      const res = await signup({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: 'jobseeker',
      });
      setSubmitted(res.message);
    } catch (err) {
      if (err instanceof ApiError && err.fields) setErrors(err.fields);
      else toast.error(err instanceof ApiError ? err.message : 'Signup failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await resendVerification(formData.email);
      toast.success(res.message);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send verification email.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-cream">
      <aside className="relative bg-cream px-8 py-12 lg:px-16 lg:py-20 flex flex-col justify-between">
        {/* Quiet Tamil glyph — a small editorial whisper, not a hero */}
        <span
          aria-hidden="true"
          className="hidden lg:block absolute top-8 right-10 select-none pointer-events-none"
          style={{
            fontFamily: '"Noto Serif Tamil", Georgia, serif',
            fontSize: '72px',
            fontWeight: 500,
            color: 'var(--color-ink)',
            opacity: 0.18,
            lineHeight: 1,
          }}
        >
          வே
        </span>

        <Link
          to="/"
          aria-label="Velaigal home"
          className="text-ink no-underline self-start relative z-10"
        >
          <Wordmark variant="lockup" size="md" animateOnMount />
        </Link>

        <div className="lg:max-w-md mt-12 lg:mt-0 relative z-10">
          <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
            Free, forever
          </span>
          <h1
            className="font-display text-3xl lg:text-5xl font-medium text-ink leading-tight tracking-tight mt-4 velaigal-headline-hover"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            Real tasks, <span className="text-saffron">not trick questions.</span>
          </h1>
          <span aria-hidden="true" className="block w-12 h-px bg-saffron mt-6" />
          <p className="mt-6 text-base lg:text-lg text-ink-secondary leading-relaxed">
            Every job you apply to comes with a real task built for that
            role. Submit actual work alongside your resume, and let a
            recruiter judge what you can do.
          </p>
        </div>

        <span className="hidden lg:block text-[11px] uppercase tracking-widest text-ink-tertiary mt-12">
          Free for job seekers, always.
        </span>
      </aside>

      <section className="bg-paper flex items-center justify-center px-6 py-12 lg:px-16 lg:py-20 border-t lg:border-t-0 lg:border-l border-hairline">
        <div className="w-full max-w-sm">
          {submitted ? (
            <div className="text-center">
              <Wordmark variant="mark" size="lg" />
              <h1
                className="font-display text-2xl font-medium text-ink mt-8 leading-tight"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
              >
                Check your email
              </h1>
              <p className="mt-4 text-sm text-ink-secondary leading-relaxed">{submitted}</p>

              <div className="mt-8 flex flex-col gap-3">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="btn-secondary w-full"
                >
                  {resending ? 'Sending…' : 'Resend verification email'}
                </button>
                <Link to="/login" className="btn-primary w-full">
                  Go to sign in
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
                  Create account
                </span>
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-5">
                {[
                  { name: 'name', label: 'Your name', placeholder: 'Your full name', type: 'text' },
                  { name: 'email', label: 'Email', placeholder: 'meera@example.com', type: 'email' },
                  { name: 'password', label: 'Password', placeholder: 'At least 10 characters', type: 'password' },
                  { name: 'confirmPassword', label: 'Confirm password', placeholder: 'Repeat password', type: 'password' },
                ].map((field) => (
                  <div key={field.name} className="space-y-2">
                    <label htmlFor={field.name} className="block text-sm font-medium text-ink">
                      {field.label}
                    </label>
                    <input
                      id={field.name}
                      name={field.name}
                      type={field.type}
                      value={(formData as Record<string, string>)[field.name]}
                      onChange={handleChange}
                      placeholder={field.placeholder}
                      aria-invalid={!!errors[field.name]}
                      className="input"
                      style={errors[field.name] ? { borderColor: 'var(--color-error)' } : undefined}
                    />
                    {errors[field.name] && (
                      <p role="alert" className="text-xs text-error font-medium">
                        {errors[field.name]}
                      </p>
                    )}
                  </div>
                ))}

                <button
                  type="submit"
                  disabled={busy}
                  className="btn-primary w-full mt-8"
                >
                  {busy ? (
                    <>
                      <span
                        className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
                        style={{
                          borderColor: 'rgba(244, 240, 232, 0.35)',
                          borderTopColor: 'var(--color-cream)',
                        }}
                        aria-hidden="true"
                      />
                      <span>Creating account</span>
                    </>
                  ) : (
                    'Create account'
                  )}
                </button>
              </form>

              <p className="mt-8 text-center text-sm text-ink-tertiary">
                Already on Velaigal?{' '}
                <Link
                  to="/login"
                  className="velaigal-link font-medium"
                >
                  Sign in →
                </Link>
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default JobSeekerSignup;
